import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { imageApi } from '../services/api';
import { useTaskStore } from '../store/taskStore';
import { Upload, X } from 'lucide-react';

interface ImageUploadProps {
  taskId: number;
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export default function ImageUpload({ taskId }: ImageUploadProps) {
  const { fetchTasks, addToast } = useTaskStore();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    // Validate files
    const invalidFiles = acceptedFiles.filter(
      file => !ALLOWED_TYPES.includes(file.type) || file.size > MAX_SIZE
    );

    if (invalidFiles.length > 0) {
      addToast({
        type: 'error',
        message: 'Bazı dosyalar geçersiz format veya boyutta',
      });
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      await imageApi.uploadImages(taskId, acceptedFiles, (prog) => {
        setProgress(prog.percentage);
      });
      await fetchTasks();
      addToast({ type: 'success', message: `${acceptedFiles.length} görsel yüklendi` });
    } catch (error: any) {
      addToast({ type: 'error', message: error.message });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, [taskId, fetchTasks, addToast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'],
    },
    maxSize: MAX_SIZE,
    maxFiles: 10,
    disabled: uploading,
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-soft-border hover:border-primary-500 hover:bg-gray-50'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-soft-muted" />
        {uploading ? (
          <div>
            <p className="text-soft-text mb-2">Yükleniyor... {progress}%</p>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : (
          <div>
            <p className="text-soft-text mb-1">
              {isDragActive
                ? 'Dosyaları buraya bırakın'
                : 'Görselleri sürükleyin veya tıklayın'}
            </p>
            <p className="text-sm text-soft-muted">
              PNG, JPG, GIF, WEBP (Max 5MB, 10 dosya)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
